/*
 * Inventory & Procurement policy (v0.10.0).
 * Pure, deterministic rules: quantities are derived from immutable stock movements; status from minimum levels;
 * cost basis is weighted average cost (documented in specs/INVENTORY-PROCUREMENT.md). No money totals live here —
 * checkout totals stay in checkout-policy (Finance).
 */
import type {
  InventoryBalanceProjection, InventoryMovementProjection, InventoryPolicy, StockItemProjection, StockItemType, StockItemView, StockMovementType, StockStatus, StockUnit, SupplierProjection
} from './contracts.js';

export const UNIT_LABELS: Record<StockUnit, string> = { pcs: 'adet', ml: 'ml', l: 'litre', g: 'gr', kg: 'kg', pack: 'paket', box: 'kutu' };
export const ITEM_TYPE_LABELS: Record<StockItemType, string> = { consumable: 'Sarf', retail: 'Perakende', both: 'Sarf + Perakende' };
export const STATUS_LABELS: Record<StockStatus, string> = { ok: 'Normal', low: 'Azalıyor', critical: 'Kritik', out: 'Stok Yok' };
export const MOVEMENT_LABELS: Record<StockMovementType, string> = { opening: 'Açılış stoğu', purchase: 'Stok girişi', consumption: 'Hizmet sarfı', retail_sale: 'Perakende satış', waste: 'Fire / zayi', adjustment_in: 'Düzeltme (+)', adjustment_out: 'Düzeltme (−)', customer_return: 'Müşteri iadesi' };

export const defaultInventoryPolicy = (): InventoryPolicy => ({ retailShortage: 'block', serviceShortage: 'warn', valuationMethod: 'weighted_average', version: 1 });

/* Quantities are kept to 3 decimals so ml/gr recipes and count differences never accumulate float noise. */
export const roundQuantity = (value: number): number => Math.round(value * 1000) / 1000;

export function stockStatusFor(onHand: number, minimumStock: number): StockStatus {
  if (onHand <= 0) return 'out';
  if (minimumStock <= 0) return 'ok';
  if (onHand <= minimumStock / 2) return 'critical';
  if (onHand <= minimumStock) return 'low';
  return 'ok';
}

export function onHandFor(itemId: string, movements: InventoryMovementProjection[]): number {
  return roundQuantity(movements.filter((m) => m.catalogItemId === itemId).reduce((n, m) => n + m.quantityDelta, 0));
}

export function deriveBalances(items: StockItemProjection[], movements: InventoryMovementProjection[]): InventoryBalanceProjection[] {
  return items.map((item) => { const onHand = onHandFor(item.id, movements); return { catalogItemId: item.id, itemName: item.name, onHand, unitCode: item.unitCode, threshold: item.minimumStock, status: stockStatusFor(onHand, item.minimumStock), version: item.version }; });
}

export function projectStockItem(item: StockItemProjection, movements: InventoryMovementProjection[], suppliers: SupplierProjection[]): StockItemView {
  const onHand = onHandFor(item.id, movements);
  return { ...item, onHand, status: stockStatusFor(onHand, item.minimumStock), supplierName: suppliers.find((s) => s.id === item.preferredSupplierId)?.name ?? null, stockValueMinor: Math.round(Math.max(0, onHand) * item.averageCostMinor) };
}

/* Weighted average cost: (onHand × avg + qty × cost) / (onHand + qty); an empty or negative balance restarts at the purchase cost. */
export function weightedAverageCost(onHand: number, averageCostMinor: number, quantity: number, unitCostMinor: number): number {
  if (quantity <= 0) return averageCostMinor;
  if (onHand <= 0 || averageCostMinor <= 0) return Math.round(unitCostMinor);
  return Math.round((onHand * averageCostMinor + quantity * unitCostMinor) / (onHand + quantity));
}

export function validateStockItem(command: { name: string; sku: string; unitCode: StockUnit; itemType: StockItemType; minimumStock: number; retailPriceMinor: number | null; openingQuantity?: number; openingUnitCostMinor?: number }, existingSkus: string[]): string | null {
  if (command.name.trim().length < 2) return 'Ürün adı en az 2 karakter olmalıdır.';
  if (!command.sku.trim()) return 'Stok kodu (SKU) gereklidir.';
  if (existingSkus.includes(command.sku.trim().toLocaleUpperCase('tr-TR'))) return 'Bu stok kodu başka bir üründe kullanılıyor.';
  if (!(command.unitCode in UNIT_LABELS)) return 'Birim geçersiz.';
  if (!(command.itemType in ITEM_TYPE_LABELS)) return 'Ürün tipi geçersiz.';
  if (!Number.isFinite(command.minimumStock) || command.minimumStock < 0) return 'Minimum stok negatif olamaz.';
  if (command.itemType !== 'consumable' && (command.retailPriceMinor === null || !Number.isInteger(command.retailPriceMinor) || command.retailPriceMinor <= 0)) return 'Perakende ürünler için satış fiyatı gereklidir.';
  if (command.openingQuantity !== undefined && (!Number.isFinite(command.openingQuantity) || command.openingQuantity < 0)) return 'Açılış stoğu negatif olamaz.';
  if (command.openingUnitCostMinor !== undefined && (!Number.isInteger(command.openingUnitCostMinor) || command.openingUnitCostMinor < 0)) return 'Birim maliyet geçersiz.';
  return null;
}

export function validateSupplier(command: { name: string; phone: string; email: string }): string | null {
  if (command.name.trim().length < 2) return 'Tedarikçi adı en az 2 karakter olmalıdır.';
  if (command.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(command.email)) return 'E-posta adresi geçersiz.';
  return null;
}

export function validateInventoryPolicy(policy: InventoryPolicy): string | null {
  if (!['block', 'warn'].includes(policy.retailShortage) || !['block', 'warn'].includes(policy.serviceShortage)) return 'Stok yetersizliği davranışı geçersiz.';
  if (policy.valuationMethod !== 'weighted_average') return 'Değerleme yöntemi desteklenmiyor.';
  return null;
}
